<template>
  <BaseQuestion
    :question-text="getQuestionText"
    :tooltip="getHoverInfo"
  >
    <img
      class="smiley"
      :src="getSmileyPicture"
      alt=""
    />
    <div class="slide-container">
      <div class="step-markers" aria-hidden="true">
        <span v-for="i in 5" :key="i" class="step-marker"
          :class="{ 'step-marker--active': hasAnswer && answer === i }" />
      </div>
      <input
        ref="sliderRef"
        :value="sliderValue"
        type="range"
        min="1"
        max="5"
        step="1"
        class="slider-input"
        :class="{ 'slider-input--idle': !hasAnswer }"
        aria-label="Rate from 1 to 5"
        @input="onInput"
        @pointerup="onCommit"
      />
      <ul class="datalist" aria-hidden="true">
        <li class="datalist-item" data-pos="0" data-num="1">
          <span class="datalist-label">{{ $t('SliderQuestion.datalistOptions.stronglyDisagree') }}</span>
        </li>
        <li class="datalist-item" data-pos="1" data-num="2">
          <span class="datalist-label">{{ $t('SliderQuestion.datalistOptions.disagree') }}</span>
        </li>
        <li class="datalist-item" data-pos="2" data-num="3">
          <span class="datalist-label">{{ $t('SliderQuestion.datalistOptions.indifferent') }}</span>
        </li>
        <li class="datalist-item" data-pos="3" data-num="4">
          <span class="datalist-label">{{ $t('SliderQuestion.datalistOptions.agree') }}</span>
        </li>
        <li class="datalist-item" data-pos="4" data-num="5">
          <span class="datalist-label">{{ $t('SliderQuestion.datalistOptions.stronglyAgree') }}</span>
        </li>
      </ul>
      <div class="slider-legend" aria-hidden="true">
        <span>1 = {{ $t('SliderQuestion.datalistOptions.stronglyDisagree') }}</span>
        <span>5 = {{ $t('SliderQuestion.datalistOptions.stronglyAgree') }}</span>
      </div>
    </div>
  </BaseQuestion>
</template>

<script>
import { applyExternalEvalSubjectNameInQuestionText } from '@/helpers/externalEvalQuestionText';
import BaseQuestion from '@/components/Home/QuestionnaireComponent/questions/BaseQuestion.vue';

// Vite: preload smiley images (answer 1-5, locale en/de) as URLs
const smileyModules = import.meta.glob('@/assets/smiley-*.png', { eager: true, query: '?url', import: 'default' });
const smileyKeys = Object.keys(smileyModules);

function getSmileyUrl(answer, locale) {
  const a = answer ?? 0;
  const key = smileyKeys.find((k) => k.includes(`smiley-${a}-${locale}`))
    || smileyKeys.find((k) => k.includes(`smiley-${a}.`));
  return key ? smileyModules[key] : smileyModules[smileyKeys[0]];
}

export default {
  name: 'SliderQuestion',
  components: { BaseQuestion },
  computed: {
    getQuestionText() {
      const question = this.$store.state.questionnaireManagement.selectedQuestion;
      if (!question?.properties?.content?.text_question) {
        return 'Question not available';
      }
      const questionText = question.properties.content.text_question[this.getLocale] ?? '';
      return applyExternalEvalSubjectNameInQuestionText(questionText, this.getCreatorName);
    },
    hasAnswer() {
      const v = this.$store.state.questionnaireManagement.answer;
      return v != null && v !== '';
    },
    getSmileyPicture() {
      return this.hasAnswer
        ? getSmileyUrl(this.answer, this.getLocale)
        : getSmileyUrl(0, this.getLocale);
    },
    getHoverInfo() {
      const question = this.$store.state.questionnaireManagement.selectedQuestion;
      return question?.properties?.content?.description?.[this.getLocale] ?? null;
    },
    getLocale() {
      return this.$i18n.locale;
    },
    getCreatorName() {
      return this.$store.state.questionnaireManagement.assessment?.creator?.name;
    },
    answer() {
      const v = this.$store.state.questionnaireManagement.answer;
      if (v == null) return null;
      const num = Number(v);
      return Math.min(5, Math.max(1, num));
    },
    sliderValue() {
      return this.hasAnswer ? this.answer : 3;
    },
  },
  methods: {
    onInput(e) {
      this.$store.commit('setAnswer', Number(e.target.value));
    },
    onCommit() {
      this.$nextTick(() => {
        const val = Number(this.$refs.sliderRef.value);
        this.$store.commit('setAnswer', val);
      });
    },
  },
};
</script>

<style scoped>
.smiley {
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: clamp(0.25rem, 1vh, 1rem);
  width: max(120px, min(35%, 28vh));
  max-width: 100%;
}

.slide-container {
  width: 100%;
  max-width: 100%;
  touch-action: none;
  position: relative;
  z-index: 0;
  padding: 0 16px;
  box-sizing: border-box;
}

.step-markers {
  position: absolute;
  top: 0;
  left: 16px;
  right: 16px;
  height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 0;
}

.step-marker {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-light-grey, #e7e7e7);
  transition: background 0.2s ease;
}

.step-marker--active {
  background: var(--color-orange, #e4821a);
}

.slider-input {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 40px;
  outline: none;
  cursor: pointer;
  position: relative;
  z-index: 1;
  touch-action: none;
  pointer-events: auto;
  background: transparent;
}

/* WebKit: runnable track (the bar) - must be defined so thumb is interactive */
.slider-input::-webkit-slider-runnable-track {
  -webkit-appearance: none;
  height: 10px;
  border-radius: 9999px;
  background: var(--color-grey);
  border: none;
}

.slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(145deg, #fff 0%, var(--color-light-grey) 100%);
  cursor: grab;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2), 0 0 0 2px var(--color-orange);
  border: none;
  margin-top: -11px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.slider-input::-webkit-slider-thumb:active {
  cursor: grabbing;
}

.slider-input::-webkit-slider-thumb:hover {
  transform: scale(1.05);
  box-shadow: 0 3px 14px rgba(228, 130, 26, 0.35), 0 0 0 3px var(--color-orange);
}

/* Firefox: track */
.slider-input::-moz-range-track {
  height: 10px;
  border-radius: 9999px;
  background: var(--color-grey);
  border: none;
}

.slider-input::-moz-range-thumb {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2), 0 0 0 2px var(--color-orange);
  background: linear-gradient(145deg, #fff 0%, var(--color-light-grey) 100%);
  cursor: grab;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.slider-input::-moz-range-thumb:active {
  cursor: grabbing;
}

.slider-input::-moz-range-thumb:hover {
  transform: scale(1.05);
  box-shadow: 0 3px 14px rgba(228, 130, 26, 0.35), 0 0 0 3px var(--color-orange);
}

/* Idle state: thumb is faded when no answer selected */
.slider-input--idle::-webkit-slider-thumb {
  opacity: 0.35;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1), 0 0 0 1px var(--color-grey);
}

.slider-input--idle::-moz-range-thumb {
  opacity: 0.35;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1), 0 0 0 1px var(--color-grey);
}

/* Labels: flex row so each label owns an equal column and long text wraps */
.datalist {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin: 0;
  padding: clamp(0.375rem, 1vw, 0.5rem) 0 clamp(0.5rem, 1.5vw, 1rem);
  list-style: none;
  box-sizing: border-box;
  gap: 2px;
}

.datalist-item {
  flex: 2 1 0;
  min-width: 0;
  list-style: none;
  list-style-type: none;
  padding: 0 0.125rem;
  font-size: clamp(0.75rem, 1vw + 0.25rem, 1.125rem);
  font-weight: 500;
  color: #1E2019;
  box-sizing: border-box;
  pointer-events: none;
  display: flex;
  justify-content: center;
  line-height: 1.3;
}

.datalist-item::marker {
  content: none;
}

.datalist-label {
  text-align: left;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.datalist-item:first-child {
  flex: 1 1 0;
  justify-content: flex-start;
}

.datalist-item:last-child {
  flex: 1 1 0;
  justify-content: flex-end;
}

.datalist-item:last-child .datalist-label {
  text-align: right;
}

/* Hidden on desktop */
.slider-legend {
  display: none;
}

/* ---- Mobile responsive ---- */
@media (max-width: 1023px) {
  .datalist-item {
    font-size: 0;
  }

  .datalist-item::after {
    content: attr(data-num);
    font-size: 0.9375rem;
    font-weight: 600;
    color: #1E2019;
  }

  .slider-legend {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0 0;
    font-size: 0.75rem;
    font-style: italic;
    font-weight: 400;
    color: #9ca3af;
    border-top: 1px solid var(--color-light-grey, #e7e7e7);
    margin-top: 0.25rem;
  }

  .slider-input::-webkit-slider-thumb {
    width: 28px;
    height: 28px;
    margin-top: -9px;
  }

  .slider-input::-moz-range-thumb {
    width: 28px;
    height: 28px;
  }

  .step-marker {
    width: 8px;
    height: 8px;
  }
}

@media (max-width: 480px) {
  .slider-legend {
    font-size: 0.75rem;
  }
}
</style>
